/**
 * Device Fingerprint Service
 *
 * Generates a unique (but anonymous) identifier for the device/browser combination.
 * Used for automatic session recovery without requiring login.
 *
 * Privacy considerations:
 * - No personal data is collected
 * - Hash is not reversible
 * - Only used for session continuity
 */

export interface BrowserInfo {
  user_agent: string
  platform: string
  language: string
  screen_resolution: string
  timezone: string
  color_depth: number
  device_memory: number | null
  hardware_concurrency: number | null
  touch_support: boolean
}

export interface DeviceRecord {
  fingerprint: string
  browser_info: BrowserInfo
  first_seen_at: string
  last_seen_at: string
}

/**
 * Simple hash function for string (djb2 algorithm)
 * Not cryptographic, but sufficient for fingerprinting
 */
function hashString(str: string): string {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash = hash & hash // Convert to 32bit integer
  }
  // Convert to base36 for shorter string
  return Math.abs(hash).toString(36).toUpperCase()
}

/**
 * Get comprehensive browser information
 */
export function getBrowserInfo(): BrowserInfo {
  return {
    user_agent: navigator.userAgent,
    platform: navigator.platform || 'unknown',
    language: navigator.language || 'unknown',
    screen_resolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    color_depth: screen.colorDepth || 24,
    device_memory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory || null,
    hardware_concurrency: navigator.hardwareConcurrency || null,
    touch_support: 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }
}

/**
 * Generate a unique fingerprint for this device/browser combination
 * Uses multiple signals to create a reasonably unique identifier
 */
export function generateDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}`,
    screen.colorDepth?.toString() || '24',
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || 'unknown',
    navigator.platform || 'unknown',
    // Canvas fingerprint (simplified - just check support)
    (typeof HTMLCanvasElement !== 'undefined').toString(),
    // WebGL fingerprint (simplified)
    (typeof WebGLRenderingContext !== 'undefined').toString(),
    // Touch support
    ('ontouchstart' in window).toString(),
    navigator.maxTouchPoints?.toString() || '0'
  ]

  return hashString(components.join('|'))
}

/**
 * Create a device record for storage
 */
export function createDeviceRecord(): DeviceRecord {
  const now = new Date().toISOString()
  return {
    fingerprint: generateDeviceFingerprint(),
    browser_info: getBrowserInfo(),
    first_seen_at: now,
    last_seen_at: now
  }
}

/**
 * Update last_seen_at for an existing device record
 */
export function updateDeviceRecord(record: DeviceRecord): DeviceRecord {
  return {
    ...record,
    last_seen_at: new Date().toISOString()
  }
}

/**
 * Check if current device matches a fingerprint
 */
export function isCurrentDevice(fingerprint: string): boolean {
  return generateDeviceFingerprint() === fingerprint
}

/**
 * Get a friendly device description for display
 */
export function getDeviceDescription(browserInfo: BrowserInfo): string {
  const ua = browserInfo.user_agent.toLowerCase()

  // Detect browser
  let browser = 'Navegador'
  if (ua.includes('chrome') && !ua.includes('edg')) browser = 'Chrome'
  else if (ua.includes('firefox')) browser = 'Firefox'
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari'
  else if (ua.includes('edg')) browser = 'Edge'
  else if (ua.includes('opera') || ua.includes('opr')) browser = 'Opera'

  // Detect OS/Device
  let device = 'Desktop'
  if (ua.includes('iphone')) device = 'iPhone'
  else if (ua.includes('ipad')) device = 'iPad'
  else if (ua.includes('android')) {
    device = browserInfo.touch_support ? 'Android' : 'Android Tablet'
  }
  else if (ua.includes('mac')) device = 'Mac'
  else if (ua.includes('windows')) device = 'Windows'
  else if (ua.includes('linux')) device = 'Linux'

  return `${browser} / ${device}`
}

/**
 * Storage key for local fingerprint cache
 */
const FINGERPRINT_STORAGE_KEY = 'lumen_device_fingerprint'

/**
 * Get or create a stable fingerprint for this device
 * Caches in localStorage to ensure consistency across sessions
 */
export function getStableFingerprint(): string {
  try {
    const stored = localStorage.getItem(FINGERPRINT_STORAGE_KEY)
    if (stored) {
      return stored
    }

    const fingerprint = generateDeviceFingerprint()
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, fingerprint)
    return fingerprint
  } catch {
    // If localStorage is not available, generate fresh each time
    return generateDeviceFingerprint()
  }
}
